import React, { useState, useEffect } from 'react';
import {
  RefreshCw,
  Building,
  User,
  Phone,
  AlertCircle,
  Edit2,
  X,
  Save,
  Plus,
  ChevronUp,
  ChevronDown
} from 'lucide-react';
import { useAuth, useToken } from '@/store/hooks';

interface Representative {
  user_id: string;
  member_id: string | null;
  name: string;
  phone: string | null;
  email?: string | null;
}

interface DepartmentHeadRow {
  id: string;
  name: string;
  code: string;
  representatives: Representative[];
  /** First rep after sort — legacy fields for older consumers */
  head_user_id: string | null;
  head_member_id: string | null;
  head_name: string | null;
  head_phone: string | null;
}

interface UserOption {
  id: string;
  member_id: string;
  first_name: string;
  last_name: string;
  phone: string;
  role?: string;
}

const DepartmentHeadsPage = () => {
  const { user } = useAuth();
  const token = useToken();
  const [rows, setRows] = useState<DepartmentHeadRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<DepartmentHeadRow | null>(null);
  /** Order preserved: first id is stored as primary head_user_id on the department */
  const [selectedRepIds, setSelectedRepIds] = useState<string[]>([]);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isAdmin = user?.role === 'universal_admin' || user?.role === 'office_admin';

  useEffect(() => {
    if (isAdmin) {
      fetchDepartmentHeads();
    }
  }, [isAdmin]);

  const normalizeRow = (raw: any): DepartmentHeadRow => {
    const reps: Representative[] = Array.isArray(raw.representatives)
      ? raw.representatives
      : [];
    return {
      id: raw.id,
      name: raw.name,
      code: raw.code,
      representatives: reps,
      head_user_id: raw.head_user_id ?? null,
      head_member_id: raw.head_member_id ?? null,
      head_name: raw.head_name ?? null,
      head_phone: raw.head_phone ?? null
    };
  };

  const fetchDepartmentHeads = async () => {
    setIsLoading(true);
    setFetchError(null);
    try {
      const response = await fetch('/api/v1/departments/heads/list', {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setRows((data.data || []).map(normalizeRow));
      } else {
        const errData = await response.json().catch(() => ({}));
        setFetchError(errData.message || `Request failed (${response.status}). Check that the API is running.`);
      }
    } catch (error) {
      console.error('Error fetching department heads:', error);
      setFetchError('Network error. Make sure the backend is running and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUsers = async () => {
    setUsersLoading(true);
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      } as const;
      const response = await fetch('/api/v1/users?role=department_head&limit=100&status=active', { headers });
      if (!response.ok) return;
      const data = await response.json();
      const list: UserOption[] = data.data?.users || [];
      setUsers(
        [...list].sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
        )
      );
    } catch (e) {
      console.error('Error fetching users:', e);
    } finally {
      setUsersLoading(false);
    }
  };

  const openEditModal = (row: DepartmentHeadRow) => {
    setEditingRow(row);
    setSelectedRepIds(row.representatives.map((r) => r.user_id));
    setSaveError(null);
    setEditModalOpen(true);
    fetchUsers();
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    setSelectedRepIds([]);
    setSaveError(null);
  };

  const removeRep = (userId: string) => {
    setSelectedRepIds((prev) => prev.filter((id) => id !== userId));
  };

  const addRepFromSelect = (userId: string) => {
    if (!userId || selectedRepIds.includes(userId)) return;
    setSelectedRepIds((prev) => [...prev, userId]);
  };

  const moveRep = (index: number, dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= selectedRepIds.length) return;
    setSelectedRepIds((prev) => {
      const copy = [...prev];
      [copy[index], copy[next]] = [copy[next], copy[index]];
      return copy;
    });
  };

  const repLabel = (userId: string) => {
    const fromRow = editingRow?.representatives.find((r) => r.user_id === userId);
    if (fromRow) return fromRow.name;
    const u = users.find((x) => x.id === userId);
    if (u) return `${u.first_name} ${u.last_name}`.trim();
    return userId;
  };

  const repPhone = (userId: string) => {
    const fromRow = editingRow?.representatives.find((r) => r.user_id === userId);
    if (fromRow?.phone) return fromRow.phone;
    const u = users.find((x) => x.id === userId);
    return u?.phone || null;
  };

  const handleSaveHead = async () => {
    if (!editingRow || !token) return;
    setSaveLoading(true);
    setSaveError(null);
    try {
      const response = await fetch(`/api/v1/departments/${editingRow.id}/representatives`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_ids: selectedRepIds })
      });

      if (response.ok) {
        await fetchDepartmentHeads();
        closeEditModal();
      } else {
        const errData = await response.json().catch(() => ({}));
        setSaveError(errData.message || 'Failed to update representatives');
      }
    } catch (e) {
      console.error('Error saving department representatives:', e);
      setSaveError('Network error. Please try again.');
    } finally {
      setSaveLoading(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h2>
          <p className="text-gray-600">You don&apos;t have permission to access this page.</p>
        </div>
      </div>
    );
  }

  const addableUsers = users.filter((u) => !selectedRepIds.includes(u.id));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Department Representatives</h1>
              <p className="mt-2 text-gray-600">
                Each department can have multiple official representatives (department heads). They receive complaint
                notifications and appear in department workflows.                 Use Edit to add, remove, or reorder (order sets the primary contact).
              </p>
            </div>
            <button
              onClick={fetchDepartmentHeads}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {fetchError && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
            <div className="flex items-center text-red-700">
              <AlertCircle className="h-5 w-5 mr-2" />
              {fetchError}
            </div>
            <button onClick={fetchDepartmentHeads} className="text-sm font-medium text-red-700 hover:text-red-800">
              Retry
            </button>
          </div>
        )}

        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500">Loading...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      #
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Department
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Code
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Representatives
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-8 text-center text-gray-500">
                        {fetchError
                          ? 'Could not load departments. Use Retry above or check the API.'
                          : 'No departments found. Create departments from the Departments page first, then assign heads here.'}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, index) => (
                      <tr key={row.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{index + 1}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="h-4 w-4 text-gray-400 mr-2" />
                            <span className="text-sm font-medium text-gray-900">{row.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{row.code}</td>
                        <td className="px-6 py-4">
                          {row.representatives.length > 0 ? (
                            <ul className="space-y-1.5">
                              {row.representatives.map((rep) => (
                                <li key={rep.user_id} className="flex items-start text-sm text-gray-900">
                                  <User className="h-4 w-4 text-gray-400 mr-2 mt-0.5 shrink-0" />
                                  <span>
                                    {rep.name}
                                    {rep.member_id && <span className="text-gray-400 ml-1">({rep.member_id})</span>}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-400">— Not assigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {row.representatives.length > 0 ? (
                            <ul className="space-y-1">
                              {row.representatives.map((rep) => (
                                <li key={rep.user_id} className="flex items-center text-sm text-gray-900">
                                  <Phone className="h-4 w-4 text-gray-400 mr-2 shrink-0" />
                                  {rep.phone || '—'}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editModalOpen && editingRow && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50" onClick={closeEditModal} aria-hidden="true" />
            <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Representatives – {editingRow.name}</h3>
                <button type="button" onClick={closeEditModal} className="text-gray-400 hover:text-gray-600">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Add or remove department heads. Only users with the Department Head role appear in the list. Saving
                  updates assignments and notifications for this department.
                </p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Current representatives</label>
                  {selectedRepIds.length === 0 ? (
                    <p className="text-sm text-gray-500 border border-dashed border-gray-200 rounded-md px-3 py-4 text-center">
                      None — add at least one, or leave empty to clear all assignments for this department.
                    </p>
                  ) : (
                    <ol className="border border-gray-200 rounded-md divide-y divide-gray-100">
                      {selectedRepIds.map((uid, i) => (
                        <li
                          key={uid}
                          className="flex items-center justify-between gap-2 px-3 py-2 text-sm bg-white"
                        >
                          <span className="min-w-0 flex items-center gap-1">
                            <span className="inline-flex flex-col border border-gray-200 rounded shrink-0">
                              <button
                                type="button"
                                onClick={() => moveRep(i, -1)}
                                disabled={i === 0}
                                className="p-0.5 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                                title="Move up"
                              >
                                <ChevronUp className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => moveRep(i, 1)}
                                disabled={i === selectedRepIds.length - 1}
                                className="p-0.5 text-gray-600 hover:bg-gray-50 disabled:opacity-30"
                                title="Move down"
                              >
                                <ChevronDown className="h-4 w-4" />
                              </button>
                            </span>
                            <span>
                              <span className="text-gray-400 mr-2">{i + 1}.</span>
                              <span className="font-medium text-gray-900">{repLabel(uid)}</span>
                              <span className="text-gray-500 ml-2">{repPhone(uid) || '—'}</span>
                            </span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeRep(uid)}
                            className="shrink-0 text-red-600 hover:text-red-800 p-1"
                            title="Remove"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </li>
                      ))}
                    </ol>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    First in the list is the department&apos;s primary contact (stored as head on the department record).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Add representative</label>
                  <div className="flex gap-2">
                    <select
                      defaultValue=""
                      onChange={(e) => {
                        const v = e.target.value;
                        if (v) {
                          addRepFromSelect(v);
                          e.target.value = '';
                        }
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      disabled={usersLoading || addableUsers.length === 0}
                    >
                      <option value="">
                        {usersLoading ? 'Loading…' : addableUsers.length === 0 ? 'No more eligible users' : 'Choose user…'}
                      </option>
                      {addableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.first_name} {u.last_name}
                          {u.member_id ? ` (${u.member_id})` : ''} – {u.phone || '—'}
                        </option>
                      ))}
                    </select>
                    <span className="inline-flex items-center text-gray-400" title="Pick a user to append">
                      <Plus className="h-5 w-5" />
                    </span>
                  </div>
                  {usersLoading && <p className="mt-1 text-xs text-gray-500">Loading department heads…</p>}
                </div>
                {saveError && (
                  <p className="text-sm text-red-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1 shrink-0" />
                    {saveError}
                  </p>
                )}
              </div>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSaveHead}
                  disabled={saveLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {saveLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentHeadsPage;
